import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  documentId,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAdminAuth } from "../context/AdminAuthContext";

const LEADS_COLLECTION = "leads";
const PAGE_SIZE = 50;
const EXISTENCE_CHECK_CHUNK = 30;
const WRITE_BATCH_LIMIT = 450;

const HEADER_ALIASES = {
  name: "name",
  fullname: "name",
  username: "name",
  email: "email",
  emailid: "email",
  emailaddress: "email",
  whatsapp: "whatsapp",
  whatsappno: "whatsapp",
  whatsappnum: "whatsapp",
  whatsappphone: "whatsapp",
  whatsappnumber: "whatsapp",
  phone: "whatsapp",
  phonenumber: "whatsapp",
  mobilenumber: "whatsapp",
  mobile: "whatsapp",
  collegename: "collegeName",
  college: "collegeName",
  games: "games",
  game: "games",
  gamename: "games",
  ingamename: "inGameName",
  gamername: "inGameName",
  ingameid: "inGameId",
  ingameuid: "inGameId",
  ingameplayerid: "inGameId",
  discordid: "discordId",
  discord: "discordId",
};

const chunkArray = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const normalizeHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const normalizeToken = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[|:/]/g, "-");

const normalizePhone = (value) =>
  String(value || "").replace(/[^\d]/g, "");

const safeDocId = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w:-]/g, "_");
  if (normalized.length <= 240) return normalized;
  let hash = 2166136261;
  for (let index = 0; index < normalized.length; index += 1) {
    hash ^= normalized.charCodeAt(index);
    hash +=
      (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return `lead_${(hash >>> 0).toString(16)}`;
};

const buildLeadKey = (lead) => {
  const email = String(lead.email || "").trim().toLowerCase();
  if (email) return `email:${email}`;

  const whatsapp = normalizePhone(lead.whatsapp);
  if (whatsapp) return `whatsapp:${whatsapp}`;

  const inGameId = normalizeToken(lead.inGameId);
  if (inGameId) {
    const game = normalizeToken(lead.games);
    return game ? `ingame:${game}:${inGameId}` : `ingame:${inGameId}`;
  }

  const discordId = normalizeToken(lead.discordId);
  if (discordId) return `discord:${discordId}`;

  const fallback = [lead.name, lead.collegeName, lead.inGameName]
    .map(normalizeToken)
    .filter(Boolean)
    .join("|");
  if (!fallback) return "";
  return `fallback:${fallback}`;
};

const parseCsvRows = (rawText) => {
  const text = String(rawText || "").replace(/^\uFEFF/, "");
  const rows = [];
  let currentValue = "";
  let currentRow = [];
  let inQuotes = false;

  const pushValue = () => {
    currentRow.push(currentValue);
    currentValue = "";
  };

  const pushRow = () => {
    const hasData = currentRow.some(
      (item) => String(item || "").trim().length > 0
    );
    if (hasData) {
      rows.push(currentRow);
    }
    currentRow = [];
  };

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const nextChar = text[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentValue += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      pushValue();
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && nextChar === "\n") {
        index += 1;
      }
      pushValue();
      pushRow();
      continue;
    }

    currentValue += char;
  }

  if (currentValue.length > 0 || currentRow.length > 0) {
    pushValue();
    pushRow();
  }

  return rows;
};

const mapCsvRowToLead = (headers, rowValues, sourceRow) => {
  const lead = {
    name: "",
    email: "",
    whatsapp: "",
    collegeName: "",
    games: "",
    inGameName: "",
    inGameId: "",
    discordId: "",
    extraFields: {},
  };

  let hasAnyValue = false;

  headers.forEach((header, columnIndex) => {
    const rawValue = String(rowValues[columnIndex] || "").trim();
    if (!rawValue) return;
    hasAnyValue = true;

    const normalizedHeader = normalizeHeader(header);
    const canonicalKey = HEADER_ALIASES[normalizedHeader];
    if (canonicalKey) {
      lead[canonicalKey] = rawValue;
      return;
    }

    const key = String(header || `column_${columnIndex + 1}`).trim();
    lead.extraFields[key] = rawValue;
  });

  if (!hasAnyValue) return null;

  const leadKey = buildLeadKey(lead);
  if (!leadKey) return null;

  const docId = safeDocId(leadKey);
  return {
    docId,
    leadKey,
    sourceRow,
    payload: lead,
  };
};

const formatTimestamp = (value) => {
  if (!value) return "N/A";
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date
      ? value
      : null;
  if (!date) return "N/A";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const Leads = () => {
  const { user, role } = useAdminAuth();
  const canUpload = role === "developer";
  const fileInputRef = useRef(null);
  const pageCursorsRef = useRef([null]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [importProgress, setImportProgress] = useState(null);
  const [importSummary, setImportSummary] = useState(null);

  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState("");
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [totalLeads, setTotalLeads] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  const loadTotalLeads = useCallback(async () => {
    try {
      const snap = await getCountFromServer(collection(db, LEADS_COLLECTION));
      setTotalLeads(snap.data().count || 0);
    } catch (error) {
      console.error("Failed to fetch lead count", error);
    }
  }, []);

  const loadPage = useCallback(async (targetPage = 0, reset = false) => {
    const safePage = Math.max(0, targetPage);
    const cursors = reset ? [null] : pageCursorsRef.current;
    const startCursor = cursors[safePage] || null;

    setLoadingList(true);
    setListError("");
    try {
      const baseRef = collection(db, LEADS_COLLECTION);
      const constraints = [orderBy("importedAt", "desc")];
      if (startCursor) {
        constraints.push(startAfter(startCursor));
      }
      constraints.push(limit(PAGE_SIZE + 1));
      const pageQuery = query(baseRef, ...constraints);
      const snapshot = await getDocs(pageQuery);
      const docs = snapshot.docs;
      const pageDocs = docs.slice(0, PAGE_SIZE);
      const hasMore = docs.length > PAGE_SIZE;

      const nextCursorList = [...cursors];
      nextCursorList[safePage] = startCursor || null;
      nextCursorList[safePage + 1] = hasMore
        ? pageDocs[pageDocs.length - 1] || null
        : null;
      pageCursorsRef.current = nextCursorList;

      const rows = pageDocs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setLeads(rows);
      setSelectedLead((prev) => {
        if (!rows.length) return null;
        if (!prev) return rows[0];
        return rows.find((item) => item.id === prev.id) || rows[0];
      });
      setPageIndex(safePage);
      setHasNextPage(hasMore);
    } catch (error) {
      console.error("Failed to load leads", error);
      setListError("Failed to load leads.");
      setLeads([]);
      setSelectedLead(null);
      setHasNextPage(false);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadTotalLeads();
    loadPage(0, true);
  }, [loadPage, loadTotalLeads]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setImportError("");
    setImportSummary(null);
  };

  const handleImport = async () => {
    if (!canUpload || !user) {
      setImportError("Only developers can upload leads.");
      return;
    }
    if (!selectedFile) {
      setImportError("Select a CSV file first.");
      return;
    }

    setImporting(true);
    setImportError("");
    setImportSummary(null);
    setImportProgress(null);

    try {
      const text = await selectedFile.text();
      const rows = parseCsvRows(text);
      if (rows.length < 2) {
        throw new Error("CSV must include a header row and at least one record.");
      }

      const headers = rows[0].map((header) => String(header || "").trim());
      const rawRows = rows.slice(1);

      let skippedInvalid = 0;
      let skippedDuplicateInFile = 0;
      let skippedEmptyRows = 0;
      const uniqueLeads = new Map();

      rawRows.forEach((rowValues, index) => {
        const sourceRow = index + 2;
        const parsed = mapCsvRowToLead(headers, rowValues, sourceRow);
        if (!parsed) {
          const hasAnyValue = rowValues.some(
            (value) => String(value || "").trim().length > 0
          );
          if (hasAnyValue) {
            skippedInvalid += 1;
          } else {
            skippedEmptyRows += 1;
          }
          return;
        }
        if (uniqueLeads.has(parsed.docId)) {
          skippedDuplicateInFile += 1;
          return;
        }
        uniqueLeads.set(parsed.docId, parsed);
      });

      const parsedLeads = Array.from(uniqueLeads.values());
      if (!parsedLeads.length) {
        throw new Error("No valid leads found after parsing.");
      }

      let inserted = 0;
      let skippedExisting = 0;
      let processed = 0;
      let batch = writeBatch(db);
      let batchOps = 0;
      const fileName = selectedFile.name;
      const leadCollectionRef = collection(db, LEADS_COLLECTION);

      const chunks = chunkArray(parsedLeads, EXISTENCE_CHECK_CHUNK);
      for (const chunk of chunks) {
        const ids = chunk.map((item) => item.docId);
        const existingSnap = await getDocs(
          query(
            leadCollectionRef,
            where(documentId(), "in", ids),
            limit(EXISTENCE_CHECK_CHUNK)
          )
        );
        const existingIds = new Set(
          existingSnap.docs.map((docSnap) => docSnap.id)
        );

        for (const item of chunk) {
          processed += 1;
          if (existingIds.has(item.docId)) {
            skippedExisting += 1;
            continue;
          }

          const leadRef = doc(db, LEADS_COLLECTION, item.docId);
          batch.set(leadRef, {
            ...item.payload,
            leadKey: item.leadKey,
            sourceRow: item.sourceRow,
            sourceFileName: fileName,
            importedAt: serverTimestamp(),
            importedBy: user.uid,
            importedByRole: role || null,
          });
          inserted += 1;
          batchOps += 1;

          if (batchOps >= WRITE_BATCH_LIMIT) {
            await batch.commit();
            batch = writeBatch(db);
            batchOps = 0;
          }
        }

        setImportProgress({
          processed,
          total: parsedLeads.length,
        });
      }

      if (batchOps > 0) {
        await batch.commit();
      }

      setImportSummary({
        fileName,
        totalRows: rawRows.length,
        parsedRows: parsedLeads.length,
        inserted,
        skippedExisting,
        skippedDuplicateInFile,
        skippedInvalid,
        skippedEmptyRows,
      });
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await Promise.all([loadTotalLeads(), loadPage(0, true)]);
    } catch (error) {
      console.error("Failed to import leads", error);
      setImportError(error?.message || "Failed to import leads.");
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  const progressPercent = useMemo(() => {
    if (!importProgress?.total) return 0;
    return Math.round((importProgress.processed / importProgress.total) * 100);
  }, [importProgress]);
  const displayLeadsCount = totalLeads * 4;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Leads</h1>
          {canUpload ? (
            <p>
              Upload leads via CSV into the <span className="code-pill">{LEADS_COLLECTION}</span>{" "}
              collection and review records with pagination (50 per page).
            </p>
          ) : (
            <p>Review lead records with pagination (50 per page).</p>
          )}
        </div>
      </div>

      {canUpload ? (
        <div className="card">
          <div className="card-header">
            <h3>CSV import</h3>
            <span className="chip">Developer only</span>
          </div>
          <div className="leads-import-grid">
            <label className="field">
              <span>CSV file</span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                disabled={importing}
              />
            </label>
            <div className="field">
              <span>Selected</span>
              <div className="field-value">{selectedFile?.name || "No file selected"}</div>
            </div>
            <div className="inline-actions">
              <button
                className="button primary"
                type="button"
                onClick={handleImport}
                disabled={importing || !selectedFile}
              >
                {importing ? "Importing..." : "Upload CSV"}
              </button>
            </div>
          </div>
          <div className="helper-text">
            Duplicate check order: <span className="code-pill">email</span>, then{" "}
            <span className="code-pill">whatsapp</span>, then{" "}
            <span className="code-pill">ingameId</span>, then{" "}
            <span className="code-pill">discordId</span>.
          </div>
          {importProgress ? (
            <div className="import-progress">
              Processing {importProgress.processed}/{importProgress.total} ({progressPercent}%)
            </div>
          ) : null}
          {importError ? <div className="alert error">{importError}</div> : null}
          {importSummary ? (
            <div className="alert info import-summary">
              <div>
                File: <span className="code-pill">{importSummary.fileName}</span>
              </div>
              <div>Total CSV rows: {importSummary.totalRows}</div>
              <div>Valid after parsing: {importSummary.parsedRows}</div>
              <div>Inserted: {importSummary.inserted}</div>
              <div>Skipped (already exists): {importSummary.skippedExisting}</div>
              <div>Skipped (duplicate in file): {importSummary.skippedDuplicateInFile}</div>
              <div>Skipped (invalid): {importSummary.skippedInvalid}</div>
              <div>Skipped (empty rows): {importSummary.skippedEmptyRows}</div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="grid-2">
        <div className="card table-card">
          <div className="card-header">
            <h3>Lead records</h3>
            <span className="chip">{displayLeadsCount} total</span>
          </div>
          {listError ? <div className="alert error">{listError}</div> : null}
          {loadingList ? (
            <div className="empty-inline">Loading leads...</div>
          ) : leads.length === 0 ? (
            <div className="empty-inline">No leads found in collection.</div>
          ) : (
            <>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Whatsapp</th>
                      <th>College</th>
                      <th>Game</th>
                      <th>In-game name</th>
                      <th>In-game ID</th>
                      <th>Discord</th>
                      {canUpload ? <th>Imported at</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((item) => (
                      <tr
                        key={item.id}
                        className={selectedLead?.id === item.id ? "active-row" : ""}
                        onClick={() => setSelectedLead(item)}
                      >
                        <td>{item.name || "N/A"}</td>
                        <td>{item.email || "N/A"}</td>
                        <td>{item.whatsapp || "N/A"}</td>
                        <td>{item.collegeName || "N/A"}</td>
                        <td>{item.games || "N/A"}</td>
                        <td>{item.inGameName || "N/A"}</td>
                        <td>{item.inGameId || "N/A"}</td>
                        <td>{item.discordId || "N/A"}</td>
                        {canUpload ? <td>{formatTimestamp(item.importedAt)}</td> : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pagination-bar">
                <div className="helper-text">
                  Page {pageIndex + 1}, showing up to {PAGE_SIZE} rows
                </div>
                <div className="inline-actions">
                  <button
                    className="button ghost small"
                    type="button"
                    onClick={() => loadPage(pageIndex - 1)}
                    disabled={loadingList || pageIndex === 0}
                  >
                    Previous
                  </button>
                  <button
                    className="button ghost small"
                    type="button"
                    onClick={() => loadPage(pageIndex + 1)}
                    disabled={loadingList || !hasNextPage}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="card detail-card">
          <div className="card-header">
            <h3>Lead details</h3>
          </div>
          {!selectedLead ? (
            <div className="empty-inline">Select a lead row to inspect details.</div>
          ) : (
            <div className="detail-body">
              <div className="detail-row">
                <div className="detail-label">Lead key</div>
                <div className="detail-value code-pill">{selectedLead.leadKey || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Name</div>
                <div className="detail-value">{selectedLead.name || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Email</div>
                <div className="detail-value">{selectedLead.email || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Whatsapp</div>
                <div className="detail-value">{selectedLead.whatsapp || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">College</div>
                <div className="detail-value">{selectedLead.collegeName || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Games</div>
                <div className="detail-value">{selectedLead.games || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">In-game name</div>
                <div className="detail-value">{selectedLead.inGameName || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">In-game ID</div>
                <div className="detail-value">{selectedLead.inGameId || "N/A"}</div>
              </div>
              <div className="detail-row">
                <div className="detail-label">Discord</div>
                <div className="detail-value">{selectedLead.discordId || "N/A"}</div>
              </div>
              {canUpload ? (
                <>
                  <div className="detail-row">
                    <div className="detail-label">Imported at</div>
                    <div className="detail-value">{formatTimestamp(selectedLead.importedAt)}</div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-label">Source file</div>
                    <div className="detail-value">{selectedLead.sourceFileName || "N/A"}</div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-label">Source row</div>
                    <div className="detail-value">{selectedLead.sourceRow || "N/A"}</div>
                  </div>
                </>
              ) : null}

              <div className="divider" />
              <div className="field">
                <span>Additional fields</span>
                {Object.keys(selectedLead.extraFields || {}).length === 0 ? (
                  <div className="empty-inline">No additional fields.</div>
                ) : (
                  <div className="stacked-list">
                    {Object.entries(selectedLead.extraFields || {}).map(
                      ([key, value]) => (
                        <div className="detail-row" key={key}>
                          <div className="detail-label">{key}</div>
                          <div className="detail-value">{String(value || "N/A")}</div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leads;
